import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Search, User, X, Check, AlertCircle, Pencil, Mail, Phone, Hash } from "lucide-react";
import { US_STATES } from "@/lib/usStates";

export default function PatientsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSuper = user?.role === "admin" || user?.role === "organizer";
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", mrn: "", email: "", phone: "", gender: "", dob: "", address: "", city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [editing, setEditing] = useState(null); // patient currently being edited (modal)
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [editMsg, setEditMsg] = useState({ kind: "", text: "" });
  const nav = useNavigate();

  const load = () => api.get("/patients").then((r) => setPatients(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return patients.filter((p) => `${p.first_name} ${p.last_name} ${p.mrn} ${p.email}`.toLowerCase().includes(term));
  }, [patients, q]);

  const openEdit = (e, patient) => {
    e.preventDefault();
    e.stopPropagation();
    setEditing(patient);
    setEditForm({
      first_name: patient.first_name || "",
      last_name: patient.last_name || "",
      mrn: patient.mrn || "",
      email: patient.email || "",
      phone: patient.phone || "",
      gender: patient.gender || "",
      dob: patient.dob || "",
      address: patient.address || "",
      city: patient.city || "",
      state: patient.state || "",
      zip: patient.zip || "",
    });
    setEditMsg({ kind: "", text: "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditBusy(true); setEditMsg({ kind: "", text: "" });
    try {
      await api.patch(`/patients/${editing.id}`, editForm);
      setEditMsg({ kind: "success", text: "Saved." });
      load();
      setTimeout(() => { setEditing(null); setEditMsg({ kind: "", text: "" }); }, 700);
    } catch (err) {
      setEditMsg({ kind: "error", text: err.response?.data?.detail || "Save failed." });
    } finally { setEditBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ kind: "", text: "" });
    setBusy(true);
    try {
      const r = await api.post("/patients", form);
      setMsg({
        kind: "success",
        text: r.data?.welcome_email_sent
          ? `Patient created. A welcome email with login credentials was sent to ${form.email}.`
          : "Patient created.",
      });
      setForm({ first_name: "", last_name: "", mrn: "", email: "", phone: "", gender: "", dob: "", address: "", city: "", state: "", zip: "" });
      load();
      // Close the modal a moment later so the user can read the confirmation
      setTimeout(() => { setShowAdd(false); setMsg({ kind: "", text: "" }); }, 1800);
    } catch (err) {
      setMsg({ kind: "error", text: err.response?.data?.detail || "Failed to create patient." });
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="patients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Patients</h1>
          <p className="text-stone-500 mt-1">{patients.length} patient{patients.length !== 1 ? "s" : ""} in your care.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold transition-colors" data-testid="add-patient-button">
          <Plus className="w-4 h-4" />Add Patient
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="patient-search-input"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, MRN or email…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <Link to={`/patients/${p.id}`} key={p.id} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-brand-300 hover:shadow-soft transition-all relative group" data-testid={`patient-card-${p.id}`}>
            {isSuper && (
              <button
                onClick={(e) => openEdit(e, p)}
                data-testid={`patient-edit-btn-${p.id}`}
                title="Edit patient"
                className="absolute top-3 right-3 p-1.5 rounded-lg bg-white border border-stone-200 text-stone-500 hover:text-brand-700 hover:border-brand-300 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-100 text-brand-700 font-display font-bold flex items-center justify-center">
                {p.first_name?.[0]}{p.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900 truncate">{p.first_name} {p.last_name}</div>
                <div className="text-xs text-stone-500 truncate flex items-center gap-1.5"><Hash className="w-3 h-3" />{p.mrn}</div>
              </div>
            </div>
            <div className="mt-3 text-sm text-stone-600 space-y-1.5">
              {p.email && <div className="truncate flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-stone-400 shrink-0" />{p.email}</div>}
              {p.phone && <div className="text-stone-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-stone-400 shrink-0" />{p.phone}</div>}
            </div>
          </Link>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="add-patient-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-semibold text-stone-900">Add new patient</h3>
              <button onClick={() => setShowAdd(false)} data-testid="close-add-patient"><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[["first_name","First name*",true],["last_name","Last name*",true]].map(([k,l,req]) => (
                  <div key={k}>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">{l}</label>
                    <input data-testid={`patient-${k}-input`} required={req} value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})}
                      className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                    MRN {isAdmin ? <span className="text-stone-400 font-normal text-xs">(optional — auto-generated)</span> : <span className="text-stone-400 font-normal text-xs">(auto-generated)</span>}
                  </label>
                  <input
                    data-testid="patient-mrn-input"
                    value={form.mrn}
                    onChange={(e)=>setForm({...form, mrn: e.target.value})}
                    disabled={!isAdmin}
                    placeholder={isAdmin ? "Leave blank to auto-generate" : "Will be generated"}
                    className={`w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none ${!isAdmin ? "bg-stone-50 text-stone-400 cursor-not-allowed" : ""}`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input data-testid="patient-email-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                    placeholder="patient@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone</label>
                  <input data-testid="patient-phone-input" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Gender</label>
                  <select data-testid="patient-gender-input" value={form.gender} onChange={(e)=>setForm({...form,gender:e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
                    <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Date of birth</label>
                  <input data-testid="patient-dob-input" type="date" value={form.dob} onChange={(e)=>setForm({...form,dob:e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Address</label>
                  <input data-testid="patient-address-input" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-5">
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">City</label>
                  <input data-testid="patient-city-input" value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} placeholder="e.g. Boston" className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                </div>
                <div className="col-span-4">
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">State</label>
                  <select data-testid="patient-state-input" value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                    <option value="">--</option>
                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Zip</label>
                  <input data-testid="patient-zip-input" value={form.zip} onChange={(e)=>setForm({...form,zip:e.target.value})} placeholder="02118" maxLength={10} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" />
                </div>
              </div>
              <div className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                On create, the patient will receive a Welcome email with a temporary password and a link to the portal.
              </div>
              {msg.text && (
                <div data-testid={`add-patient-toast-${msg.kind}`} className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${msg.kind === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                  {msg.kind === "success" ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                  <span>{msg.text}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 font-medium hover:bg-stone-50">Cancel</button>
                <button data-testid="patient-submit-button" type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">{busy ? "Creating…" : "Create patient"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="edit-patient-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-semibold text-stone-900">Edit patient — {editing.first_name} {editing.last_name}</h3>
              <button onClick={() => setEditing(null)} data-testid="close-edit-patient"><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">First name</label>
                  <input data-testid="edit-patient-first-name" required value={editForm.first_name} onChange={(e)=>setEditForm({...editForm,first_name:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Last name</label>
                  <input data-testid="edit-patient-last-name" required value={editForm.last_name} onChange={(e)=>setEditForm({...editForm,last_name:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">MRN</label>
                  <input data-testid="edit-patient-mrn" value={editForm.mrn} onChange={(e)=>setEditForm({...editForm,mrn:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Email</label>
                  <input data-testid="edit-patient-email" type="email" value={editForm.email} onChange={(e)=>setEditForm({...editForm,email:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone</label>
                  <input data-testid="edit-patient-phone" value={editForm.phone} onChange={(e)=>setEditForm({...editForm,phone:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Gender</label>
                  <select data-testid="edit-patient-gender" value={editForm.gender} onChange={(e)=>setEditForm({...editForm,gender:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none"><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Date of birth</label>
                  <input data-testid="edit-patient-dob" type="date" value={editForm.dob} onChange={(e)=>setEditForm({...editForm,dob:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div><label className="text-sm font-medium text-stone-700 mb-1.5 block">Address</label>
                  <input data-testid="edit-patient-address" value={editForm.address} onChange={(e)=>setEditForm({...editForm,address:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-5"><label className="text-sm font-medium text-stone-700 mb-1.5 block">City</label>
                  <input data-testid="edit-patient-city" value={editForm.city} onChange={(e)=>setEditForm({...editForm,city:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
                <div className="col-span-4"><label className="text-sm font-medium text-stone-700 mb-1.5 block">State</label>
                  <select data-testid="edit-patient-state" value={editForm.state} onChange={(e)=>setEditForm({...editForm,state:e.target.value})} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none">
                    <option value="">--</option>
                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                  </select></div>
                <div className="col-span-3"><label className="text-sm font-medium text-stone-700 mb-1.5 block">Zip</label>
                  <input data-testid="edit-patient-zip" value={editForm.zip} onChange={(e)=>setEditForm({...editForm,zip:e.target.value})} maxLength={10} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none" /></div>
              </div>
              {editMsg.text && (
                <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${editMsg.kind === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                  {editMsg.kind === "success" ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}<span>{editMsg.text}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 font-medium hover:bg-stone-50">Cancel</button>
                <button data-testid="edit-patient-save" type="submit" disabled={editBusy} className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold disabled:opacity-60">{editBusy ? "Saving…" : "Save changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
